// DE-002 (user-POV test 2026-10-06, HIGH): selecting a project must hydrate
// the construction-plan's Project Information — the system already knows the
// name, address, carrier and structure info; the user must never re-key them.
// PURE: fills only BLANK fields (never clobbers something the user typed)
// and only maps fields the project row actually carries — never invents data.

/** Shape of the plan-scoped project option row (GET /construction-plans/projects). */
export interface PlanProjectOption {
  id: number;
  name: string;
  siteAddress?: string;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  carrierName?: string | null;
  carrierId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  structureInfo?: string | null;
}

/** Full site address from the project's address parts (no fabricated parts). */
export function fullSiteAddress(p: PlanProjectOption): string {
  const street = (p.siteAddress ?? '').trim();
  const parts = [p.city, p.state].map((s) => (s ?? '').trim()).filter(Boolean);
  const zip = (p.zipCode ?? '').trim();
  if (!street && !parts.length && !zip) return '';
  const cityState = parts.length ? parts.join(', ') : '';
  return [street, [cityState, zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

export interface PlanProjectFields {
  projectName: string;
  customer: string;
  siteAddress: string;
  latitude: string;
  longitude: string;
  structureOwner: string;
  siteName: string;
  siteIdNumber: string;
}

/**
 * Pre-fill the plan's project fields from the selected project row.
 * Only EMPTY target fields are filled; non-empty values are left untouched.
 * The project's structureInfo text pre-fills the Structure Owner context —
 * returned separately as a note so the UI can label it as project-sourced.
 */
export function hydratePlanProjectFields(
  current: PlanProjectFields,
  project: PlanProjectOption | null | undefined,
): PlanProjectFields & { structureInfoNote: string | null } {
  if (!project) return { ...current, structureInfoNote: null };
  const next: PlanProjectFields = { ...current };
  if (!next.projectName.trim()) next.projectName = project.name ?? '';
  if (!next.customer.trim() && project.carrierName) next.customer = project.carrierName;
  if (!next.siteAddress.trim()) next.siteAddress = fullSiteAddress(project);
  if (!next.latitude.trim() && project.latitude != null) next.latitude = String(project.latitude);
  if (!next.longitude.trim() && project.longitude != null) next.longitude = String(project.longitude);
  if (!next.structureOwner.trim() && project.structureInfo) next.structureOwner = project.structureInfo;
  // siteName / siteIdNumber: the project record carries no such fields —
  // they stay blank rather than being invented from other fields.
  const structureInfoNote = project.structureInfo?.trim() || null;
  return { ...next, structureInfoNote };
}
